// Справочник спецодежды в настройках (тз, раздел 5.5: «Их заводит администратор в настройках»).
//
// ## Зачем экран
//
// Справочник заполнялся только миграцией: заказчик не мог добавить ни куртку, ни ботинки, и
// выдавали ему ровно то, что мы засеяли при развёртывании. Карточка человека при этом честно
// писала «в справочнике нет ни одной позиции, их заводит администратор в настройках» — и
// отправляла на экран, где ничего этого не было.
//
// ## Почему у цены есть дата, а у названия нет
//
// Цена действует С УКАЗАННОГО ДНЯ и на всё, что было раньше, не распространяется. Поставил
// 4 200 двадцатого сентября — девятнадцатое по-прежнему 3 640, и долг, заведённый
// девятнадцатого, не меняется ни на рубль. Требование владельца 16.09.2026.
//
// Отсюда два следствия на экране. Первое: день начала — обязательное поле, а не подстановка
// «сегодня»: подставить сегодняшнее число значило бы молча соврать про вчерашние выдачи в тот
// единственный раз, когда цену вводят задним числом. Второе: поменять цену можно ТОЛЬКО через
// это поле — в форме правки позиции цены нет вовсе, иначе путей поменять её было бы два, и
// один из них про дату не знал бы.
//
// Рядом видно историю: «почему у соседа та же куртка дешевле» спрашивают человека из
// финотдела, а не нас, и без истории ответить ему нечем.
//
// С названием ровно наоборот, и это тоже верно. Строка выдачи хранит ссылку на позицию, а имя
// берёт из справочника — значит исправленная опечатка чинится и в уже заведённых долгах. Ради
// этого правка названия и открыта: своё название позиции человек видит в СВОЁМ долге, и
// «Ботинги зимние» там до 15.09.2026 оставались навсегда — экран давал только «Поменять цену».

import { useState } from 'react';

import { RiArrowDownSLine, RiTShirtLine } from '@remixicon/react';

import type { UniformItem, UniformPrice } from '@/shared/api/endpoints/losses';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';
import { cn } from '@/utils/cn';

import { DateField, todayIso } from '@/shared/ui/date-field';
import { formatDateOnly } from '@/shared/time/format';

import {
  useCancelUniformPrice,
  useChangeUniformPrice,
  useSaveUniformItem,
  useSetUniformItemActive,
  useUniformCatalog,
  useUniformPrices,
} from './queries';

const ПРАВО_ПРАВИТЬ = 'core.dictionary.edit';

export function СправочникСпецодежды() {
  const каталог = useUniformCatalog();
  const права = useAuthStore((состояние) => состояние.permissions);
  const можно = can(права, ПРАВО_ПРАВИТЬ);
  const [заводим, setЗаводим] = useState(false);

  // Пояснение блока короткое НАМЕРЕННО: длинное он обрезает многоточием, и обрезается как раз
  // главное. Правило целиком написано под формой цены — там, где его и читают.
  return (
    <Block
      title='Цены на спецодежду'
      note='Сколько стоит каждая вещь и с какого дня. Цена действует с указанного дня и на прошлое не распространяется'
      action={
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => setЗаводим((было) => !было)}
        >
          {заводим ? 'Не заводить' : 'Завести позицию'}
        </Button.Root>
      }
    >
      {заводим && <НоваяПозиция можно={можно} onГотово={() => setЗаводим(false)} />}

      <ScreenState
        data={каталог.data}
        error={каталог.error}
        subject='справочник спецодежды'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void каталог.refetch()}
        loading={<Skeleton className='h-20 w-full' />}
        empty={
          <EmptyState
            icon={RiTShirtLine}
            title='В справочнике пусто'
            description='Пока в нём нет ни одной позиции, выдать форму нельзя ни одному человеку: цену брать неоткуда.'
          />
        }
      >
        {(данные) => <Список позиции={данные.items} можно={можно} />}
      </ScreenState>
    </Block>
  );
}

/**
 * Новая позиция справочника: название, цена и день, С КОТОРОГО эта цена действует.
 *
 * ## Почему у новой позиции спрашивается дата
 *
 * До 17.09.2026 её не спрашивали вовсе и подставляли сегодняшнюю. Выглядело разумно: позиции
 * раньше не было, значит и цены не было. На деле наоборот — заказчик заводит позицию ПОЗЖЕ, чем
 * начал её выдавать. Куртку пилот он выдаёт два года (170 выдач в его переписке), а в
 * справочнике её нет; заводит он её сегодня, а стоит она столько-то с марта 2024.
 *
 * С подстановкой «сегодня» все эти выдачи остались бы без цены навсегда, и увидеть это было бы
 * негде: в справочнике стоит правильное число, просто оно ничего не знает о прошлом.
 */
function НоваяПозиция({ можно, onГотово }: { можно: boolean; onГотово: () => void }) {
  const сохранить = useSaveUniformItem();
  const [название, setНазвание] = useState('');
  const [цена, setЦена] = useState('');
  const [сДаты, setСДаты] = useState(todayIso);
  const [запрет, setЗапрет] = useState<string | null>(null);

  return (
    <form
      className='flex flex-wrap items-end gap-2 border-b border-stroke-soft-200 py-3'
      noValidate
      onSubmit={(событие) => {
        событие.preventDefault();
        if (!можно) {
          setЗапрет('Справочники заводит администратор: цена отсюда попадает людям в долг.');
          return;
        }
        if (название.trim() === '') {
          setЗапрет('Название обязательно: его человек видит в выдаче и в своём долге.');
          return;
        }
        const разбор = parseRublesToKopecks(цена);
        if (!разбор.ok || разбор.value <= 0) {
          setЗапрет('Цена пишется числом: 16 140 или 16 140,50.');
          return;
        }
        if (сДаты === '') {
          setЗапрет(
            'Назовите день, с которого действует эта цена: без него прошлое останется без цены.',
          );
          return;
        }
        if (сДаты > todayIso()) {
          setЗапрет(
            'День начала в будущем: до него у позиции не будет цены вовсе, а по ней выдают форму.',
          );
          return;
        }
        setЗапрет(null);
        сохранить.mutate(
          // Машинный ключ позиции сервер выводит из названия сам. Человек его не вводит:
          // латиница в поле у людей, которые её не пишут, даёт «kurtka1», «kurtka2»,
          // «kurtka_new» — ровно ту кашу, ради избавления от которой справочник и заводили.
          { body: { title: название.trim(), price: разбор.value, price_since: сДаты } },
          {
            onSuccess: () => {
              setНазвание('');
              setЦена('');
              setСДаты(todayIso());
              onГотово();
            },
          },
        );
      }}
    >
      <div className='min-w-56 flex-1'>
        <TextField
          id='uniform-title'
          label='Название'
          value={название}
          onChange={setНазвание}
          placeholder='Ботинки зимние'
        />
      </div>
      <div className='w-32'>
        <TextField
          id='uniform-price'
          label='Цена'
          value={цена}
          onChange={setЦена}
          placeholder='16 140'
          inputMode='decimal'
        />
      </div>
      <div className='w-40'>
        <DateField
          id='uniform-price-since'
          label='Цена действует с'
          value={сДаты}
          onChange={setСДаты}
        />
      </div>
      <Button.Root type='submit' variant='primary' mode='filled' size='xsmall'>
        {сохранить.isPending ? 'Заводим…' : 'Завести'}
      </Button.Root>
      {/* Пояснения стоят ПОД рядом, а не под каждым полем. Подсказка под полем делает его
          выше, поля в ряду становятся разной высоты, и подписи разъезжаются по вертикали —
          на живом экране «Цена действует с» висела на полсотни точек выше «Названия». */}
      <p className='w-full max-w-prose text-paragraph-xs text-text-sub-600'>
        Выдавали и раньше? Поставьте тот день, с которого цена такая: иначе прошлые выдачи останутся
        без цены.
      </p>
      {запрет !== null && (
        <div className='w-full'>
          <Hint.Root>{запрет}</Hint.Root>
        </div>
      )}
      {сохранить.isError && (
        <div className='w-full'>
          <ErrorAlert error={сохранить.error} />
        </div>
      )}
    </form>
  );
}

/**
 * Список позиций: сначала те, что выдают, потом убранные — и только по требованию.
 *
 * ## Почему убранные не пропадают с экрана насовсем
 *
 * До 21.09.2026 снятие позиции было дверью в одну сторону: сервер его умел, экран не показывал,
 * а список всегда отбирал только действующие. Значит убранную нельзя было ни увидеть, ни
 * вернуть ничем, кроме прямого запроса к базе. Кнопка без возврата — это не «удалить», это
 * «потерять», и класть её на экран в таком виде нельзя.
 *
 * Строка «Убрано: N» стоит ПОД списком и свёрнута: убранные позиции не участвуют в работе, и
 * место в глазу занимать не должны. Но они и не исчезают: их видно числом.
 */
function Список({ позиции, можно }: { позиции: UniformItem[]; можно: boolean }) {
  const [показыватьУбранные, setПоказыватьУбранные] = useState(false);
  const действующие = позиции.filter((п) => п.is_active !== false);
  const убранные = позиции.filter((п) => п.is_active === false);

  return (
    <div className='flex flex-col'>
      <ul className='flex flex-col divide-y divide-stroke-soft-200'>
        {действующие.map((позиция) => (
          <Позиция key={позиция.id} позиция={позиция} можно={можно} />
        ))}
      </ul>

      {убранные.length > 0 && (
        <div className='flex flex-col border-t border-stroke-soft-200 pt-2'>
          <button
            type='button'
            className='flex w-fit items-center gap-1 py-1 text-paragraph-xs text-text-sub-600 hover:text-text-strong-950'
            onClick={() => setПоказыватьУбранные((было) => !было)}
          >
            {показыватьУбранные ? 'Скрыть убранные' : `Убрано из выдачи: ${убранные.length}`}
            <RiArrowDownSLine
              aria-hidden
              className={cn('size-4 transition-transform', показыватьУбранные && 'rotate-180')}
            />
          </button>
          {показыватьУбранные && (
            <ul className='flex flex-col divide-y divide-stroke-soft-200'>
              {убранные.map((позиция) => (
                <Позиция key={позиция.id} позиция={позиция} можно={можно} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Позиция({ позиция, можно }: { позиция: UniformItem; можно: boolean }) {
  const сохранить = useSaveUniformItem();
  const снятие = useSetUniformItemActive();
  const [название, setНазвание] = useState(позиция.title);
  const [порядок, setПорядок] = useState(() => String(позиция.sort_order));
  const [правим, setПравим] = useState(false);
  const [цены, setЦены] = useState(false);
  const [убираем, setУбираем] = useState(false);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const убрана = позиция.is_active === false;
  const выдавали = позиция.issues_count ?? 0;

  function отменить() {
    setНазвание(позиция.title);
    setПорядок(String(позиция.sort_order));
    setЗапрет(null);
    setПравим(false);
  }

  return (
    <li className='flex flex-wrap items-center justify-between gap-3 py-2.5'>
      {/* Только название. Машинный ключ позиции под ним стоял до 04.09.2026 — девять строк
          латиницы в настройках, которые человеку ничего не говорят и говорить не должны:
          выдачу он выбирает по названию, а ключом на цену ссылается система. */}
      <div className='flex min-w-0 flex-col'>
        <span
          className={cn(
            'text-paragraph-sm',
            убрана ? 'text-text-sub-600 line-through' : 'text-text-strong-950',
          )}
        >
          {позиция.title}
        </span>
        {/* Почему строка выглядит иначе — сказано словом, а не только бледностью: приглушённый
            текст читается и как «недоступно», и как «неважно», и как «сломалось». */}
        {убрана && (
          <span className='text-paragraph-xs text-text-sub-600'>
            убрана из выдачи, в истории осталась
          </span>
        )}
      </div>

      {правим ? (
        // Название правится ЗДЕСЬ ЖЕ, а не отдельной кнопкой: сервер принимал его с первого
        // дня, а экран отдавал только цену — опечатку в названии («Ботинги зимние») человек
        // видел в СВОЁМ долге, и починить её было нечем.
        <div className='flex flex-wrap items-end gap-2'>
          <div className='min-w-56 flex-1'>
            <TextField
              id={`uniform-title-${позиция.id}`}
              label='Название'
              value={название}
              onChange={setНазвание}
              hint='Это же название человек видит в своём долге и в выдаче.'
            />
          </div>
          <div className='w-24'>
            <TextField
              id={`uniform-order-${позиция.id}`}
              label='Порядок'
              value={порядок}
              onChange={setПорядок}
              inputMode='numeric'
              hint='Меньше — выше в списке выдачи.'
            />
          </div>
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='xsmall'
            onClick={() => {
              if (!можно) {
                setЗапрет('Справочник правит администратор: отсюда цена попадает людям в долг.');
                return;
              }
              if (название.trim() === '') {
                setЗапрет('Название стереть нельзя: его человек видит в выдаче и в своём долге.');
                return;
              }
              const место = Number(порядок);
              if (!Number.isInteger(место) || место < 0 || место > 999) {
                setЗапрет('Порядок — целое число от 0 до 999.');
                return;
              }
              setЗапрет(null);
              сохранить.mutate(
                {
                  id: позиция.id,
                  body: { title: название.trim(), sort_order: место },
                },
                { onSuccess: () => setПравим(false) },
              );
            }}
          >
            {сохранить.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={отменить}
          >
            Отмена
          </Button.Root>
        </div>
      ) : (
        <div className='flex items-center gap-3'>
          {/* Знак рубля стоит: цена идёт в фразе рядом с названием, а не столбцом. */}
          <div className='flex flex-col items-end'>
            <span className='tabular-nums text-label-sm text-text-strong-950'>
              {formatRubles(позиция.price)}
            </span>
            {/* «С какого дня» стоит ПОД ценой всегда, а не только когда её меняли: число без
                даты не отличить от прежнего, а вопрос «когда подорожало» задают одновременно
                с «сколько стоит». */}
            <span className='text-paragraph-xs text-text-sub-600'>
              {позиция.price_since === null || позиция.price_since === undefined
                ? 'дата начала неизвестна'
                : `действует с ${formatDateOnly(позиция.price_since)}`}
            </span>
          </div>
          <Button.Root
            type='button'
            variant='neutral'
            mode='ghost'
            size='xsmall'
            onClick={() => setЦены((было) => !было)}
          >
            {цены ? 'Скрыть цены' : 'Цена и история'}
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='ghost'
            size='xsmall'
            onClick={() => setПравим(true)}
          >
            Править
          </Button.Root>
          {/* Слово ЕГО, а не наше. Заказчик просил «удалить», и кнопка называется так же;
              что именно произойдёт, объясняет подтверждение ниже — числом, а не общими
              словами. Убранная позиция получает обратное действие тем же местом. */}
          {убрана ? (
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xsmall'
              onClick={() => {
                if (!можно) {
                  setЗапрет(
                    'Справочник ведёт администратор: отсюда цена попадает людям в долг. Попросите его вернуть позицию.',
                  );
                  return;
                }
                setЗапрет(null);
                снятие.mutate({ id: позиция.id, active: true, title: позиция.title });
              }}
            >
              {снятие.isPending ? 'Возвращаем…' : 'Вернуть'}
            </Button.Root>
          ) : (
            <Button.Root
              type='button'
              variant='error'
              mode='ghost'
              size='xsmall'
              onClick={() => {
                if (!можно) {
                  setЗапрет(
                    'Справочник ведёт администратор: отсюда цена попадает людям в долг. Попросите его убрать позицию.',
                  );
                  return;
                }
                setЗапрет(null);
                setУбираем(true);
              }}
            >
              Удалить
            </Button.Root>
          )}
        </div>
      )}

      {убираем && (
        <ПодтверждениеСнятия
          позиция={позиция}
          выдавали={выдавали}
          занято={снятие.isPending}
          onУбрать={() =>
            снятие.mutate(
              { id: позиция.id, active: false, title: позиция.title },
              { onSuccess: () => setУбираем(false) },
            )
          }
          onОтмена={() => setУбираем(false)}
        />
      )}

      {цены && <ЦеныПозиции позиция={позиция} можно={можно} />}

      {запрет !== null && (
        <div className='w-full'>
          <Hint.Root>{запрет}</Hint.Root>
        </div>
      )}
      {сохранить.isError && (
        <div className='w-full'>
          <ErrorAlert error={сохранить.error} />
        </div>
      )}
      {снятие.isError && (
        <div className='w-full'>
          <ErrorAlert error={снятие.error} />
        </div>
      )}
    </li>
  );
}

/**
 * Что произойдёт при нажатии «Удалить» — сказано ДО нажатия и цифрой.
 *
 * ## Почему не окно, а раскрытие в строке
 *
 * Окно в этом продукте оправдано только у необратимого. Снятие обратимо: позиция уходит в
 * «Убрано из выдачи» и возвращается оттуда одним нажатием. Окно здесь означало бы, что мы
 * считаем действие страшнее, чем оно есть, — а человек, которому дважды сказали «вы уверены?»
 * на пустом месте, перестаёт читать вопрос вообще.
 *
 * ## Почему два разных текста
 *
 * «Никому не выдавали» и «выдали двенадцать раз» — это два разных решения. В первом случае
 * позиция исчезает и не оставляет следа нигде. Во втором её название стоит в долгах живых
 * людей, и стереть его нельзя: они останутся с суммой без предмета. Одна формулировка на оба
 * случая либо пугала бы там, где бояться нечего, либо молчала бы там, где надо предупредить.
 */
function ПодтверждениеСнятия({
  позиция,
  выдавали,
  занято,
  onУбрать,
  onОтмена,
}: {
  позиция: UniformItem;
  выдавали: number;
  занято: boolean;
  onУбрать: () => void;
  onОтмена: () => void;
}) {
  const выдавалиРаз = countLabel(выдавали, ['раз', 'раза', 'раз']);
  return (
    <div className='flex w-full flex-col gap-2 rounded-lg bg-bg-weak-50 px-4 py-3'>
      <span className='text-paragraph-sm text-text-strong-950'>
        {выдавали === 0
          ? `«${позиция.title}» ещё никому не выдавали. Уберём из справочника: при выдаче её больше не предложат.`
          : `«${позиция.title}» выдавали ${выдавалиРаз}. Стереть совсем нельзя: её название стоит в долгах этих людей, и они остались бы с суммой без предмета.`}
      </span>
      <span className='text-paragraph-xs text-text-sub-600'>
        {выдавали === 0
          ? 'Передумаете — вернёте её здесь же, строкой «Убрано из выдачи» под списком.'
          : 'Уберём из выбора: новые выдачи по ней делать будет нельзя, история и долги останутся как есть. Вернуть можно здесь же.'}
      </span>
      <div className='flex flex-wrap gap-2'>
        <Button.Root type='button' variant='error' mode='filled' size='xsmall' onClick={onУбрать}>
          {занято ? 'Убираем…' : выдавали === 0 ? 'Убрать' : 'Убрать из выбора'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='xsmall' onClick={onОтмена}>
          Не убирать
        </Button.Root>
      </div>
    </div>
  );
}

/**
 * Цена позиции: как менялась и как поменять.
 *
 * ## Главное правило, ради которого экран и сделан
 *
 * Цена действует С НАЗВАННОГО ДНЯ и на всё, что было раньше, не распространяется. Поставил
 * 4 200 двадцатого сентября — девятнадцатое по-прежнему 3 640, и долг, заведённый
 * девятнадцатого, не меняется ни на рубль.
 *
 * Поэтому день начала — обязательное поле, а не подстановка «сегодня». Подставить сегодняшнее
 * число значило бы молча соврать про вчерашние выдачи в тот единственный раз, когда человек
 * вводит цену задним числом, — а он вводит её задним числом ровно тогда, когда подорожание
 * случилось раньше, чем он до программы дошёл.
 *
 * ## Почему история видна здесь же
 *
 * Вопрос «почему у соседа та же куртка дешевле» задают человеку из финотдела, а не нам. Без
 * истории ответить на него нечем: прежняя цена нигде не хранилась вовсе.
 */
function ЦеныПозиции({ позиция, можно }: { позиция: UniformItem; можно: boolean }) {
  const история = useUniformPrices(позиция.id);
  const поменять = useChangeUniformPrice();
  const [цена, setЦена] = useState(() => formatKopecks(позиция.price));
  const [сДаты, setСДаты] = useState(todayIso);
  const [почему, setПочему] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);

  return (
    <div className='w-full border-t border-stroke-soft-200 pt-3'>
      <div className='flex flex-wrap items-end gap-2'>
        <div className='w-36'>
          <TextField
            id={`price-new-${позиция.id}`}
            label='Новая цена'
            value={цена}
            onChange={setЦена}
            inputMode='decimal'
          />
        </div>
        <div className='w-40'>
          <DateField
            id={`price-from-${позиция.id}`}
            label='Действует с'
            value={сДаты}
            onChange={setСДаты}
          />
        </div>
        <div className='min-w-56 flex-1'>
          <TextField
            id={`price-why-${позиция.id}`}
            label='Почему поменяли'
            value={почему}
            onChange={setПочему}
            placeholder='подорожал поставщик, новый договор, ошибка в прайсе'
          />
        </div>
        <Button.Root
          type='button'
          variant='primary'
          mode='filled'
          size='xsmall'
          onClick={() => {
            if (!можно) {
              setЗапрет('Цену меняет администратор: отсюда она попадает людям в долг.');
              return;
            }
            const разбор = parseRublesToKopecks(цена);
            if (!разбор.ok || разбор.value <= 0) {
              setЗапрет('Цена пишется числом: 16 140 или 16 140,50.');
              return;
            }
            if (сДаты === '') {
              setЗапрет(
                'Назовите день, с которого цена действует: без него непонятно, к чему её применять.',
              );
              return;
            }
            setЗапрет(null);
            поменять.mutate({
              id: позиция.id,
              body: {
                price: разбор.value,
                effective_from: сДаты,
                ...(почему.trim() === '' ? {} : { basis: почему.trim() }),
              },
            });
          }}
        >
          {поменять.isPending ? 'Записываем…' : 'Записать цену'}
        </Button.Root>
        {/* Правило целиком стоит здесь, под формой, а не подсказкой под полем даты: подсказка
            делала поле выше соседних, и подписи в ряду разъезжались по вертикали. */}
        <p className='w-full max-w-prose text-paragraph-xs text-text-sub-600'>
          Новая цена действует с указанного дня. На всё, что было раньше, она не применяется:
          заведённые долги не меняются ни на рубль.
        </p>
      </div>
      {запрет !== null && <Hint.Root hasError>{запрет}</Hint.Root>}
      {поменять.error !== null && <ErrorAlert error={поменять.error} />}

      <ScreenState
        data={история.data}
        error={история.error}
        subject='история цен'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void история.refetch()}
        loading={<Skeleton className='mt-3 h-16 w-full' />}
        empty={
          <p className='mt-3 text-paragraph-xs text-text-sub-600'>
            Цену этой позиции ещё не меняли: она такая с того дня, как позицию завели.
          </p>
        }
      >
        {(данные) => (
          <ul className='mt-3 flex flex-col gap-1'>
            {данные.items.map((запись) => (
              <СтрокаИстории
                key={запись.id}
                запись={запись}
                позиция={позиция}
                можно={можно}
                последняя={данные.items.filter((с) => с.cancelled !== true).length <= 1}
              />
            ))}
          </ul>
        )}
      </ScreenState>
    </div>
  );
}

/**
 * Одна строка истории цен: что было, когда начало действовать и можно ли её убрать.
 *
 * ## Почему «Убрать», а не «Исправить»
 *
 * Строку не правят: правка задним числом стёрла бы то, что человек сказал раньше. Опечатку в
 * ЦЕНЕ он чинит второй записью тем же днём — действует последняя. А опечатку в ДАТЕ так не
 * починить: ошибочная строка остаётся и продолжает отвечать за свой отрезок времени, и до
 * 17.09.2026 сделать с этим было нечего вовсе.
 *
 * Убранная строка остаётся здесь зачёркнутой, с причиной. Исчезнувшая читалась бы как пропажа:
 * человек помнит, что вводил другое число, и не найдя его, идёт спрашивать нас.
 */
function СтрокаИстории({
  запись,
  позиция,
  можно,
  последняя,
}: {
  запись: UniformPrice;
  позиция: UniformItem;
  можно: boolean;
  последняя: boolean;
}) {
  const убрать = useCancelUniformPrice();
  const [убираем, setУбираем] = useState(false);
  const [причина, setПричина] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);
  const убрана = запись.cancelled === true;

  return (
    <li className='flex flex-col gap-1 text-paragraph-xs text-text-sub-600'>
      <div className='flex flex-wrap items-baseline gap-x-2'>
        {/* Убранную строку отличает ЗАЧЁРКИВАНИЕ, а не бледность. Бледно-серым в кабинете
            текст не пишут вовсе: тон soft-400 даёт на белом контраст 2,63:1 при норме 4,5, и
            это держит сторож оформления. Здесь запрет к месту и по смыслу: строку убрали, но
            читать её человек будет — он за этим в историю и пришёл. */}
        <span
          className={
            убрана
              ? 'tabular-nums text-label-xs text-text-sub-600 line-through'
              : 'tabular-nums text-label-xs text-text-strong-950'
          }
        >
          {formatRubles(запись.price)}
        </span>
        <span className={убрана ? 'line-through' : undefined}>
          с {formatDateOnly(запись.effective_from)}
        </span>
        {запись.basis !== null && запись.basis !== undefined && запись.basis !== '' && (
          <span className={убрана ? 'line-through' : undefined}>· {запись.basis}</span>
        )}
        <span className={убрана ? 'line-through' : undefined}>
          · {запись.author ?? 'перенос данных'}
        </span>
        {убрана ? (
          <span className='text-text-sub-600'>
            · убрана{запись.cancel_reason ? `: ${запись.cancel_reason}` : ''}
          </span>
        ) : (
          !убираем && (
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xsmall'
              // К правому краю: вплотную за «перенос данных» кнопка дочитывается как ещё одно
              // слово в строке, а не как действие над ней.
              className='ml-auto'
              onClick={() => {
                if (!можно) {
                  setЗапрет('Цены правит администратор: отсюда они попадают людям в долг.');
                  return;
                }
                // Последнюю действующую строку убрать нельзя, и сказать об этом надо ДО
                // нажатия: иначе человек жмёт, получает отказ сервера и не понимает, в чём
                // разница между этой строкой и соседней.
                if (последняя) {
                  setЗапрет(
                    'Это единственная действующая цена. Запишите верную с нужной датой, а потом уберите эту.',
                  );
                  return;
                }
                setЗапрет(null);
                setУбираем(true);
              }}
            >
              Убрать
            </Button.Root>
          )
        )}
      </div>

      {убираем && (
        <div className='flex flex-wrap items-end gap-2 pb-1'>
          <div className='min-w-56 flex-1'>
            <TextField
              id={`price-cancel-${запись.id}`}
              label='Почему убираем'
              value={причина}
              onChange={setПричина}
              hint='Ошиблись датой, ошиблись позицией, цена не вступила в силу.'
            />
          </div>
          <Button.Root
            type='button'
            variant='error'
            mode='stroke'
            size='xsmall'
            onClick={() => {
              if (причина.trim().length < 4) {
                setЗапрет(
                  'Напишите, почему убираете: строка остаётся в истории, и рядом с ней читают причину.',
                );
                return;
              }
              setЗапрет(null);
              убрать.mutate(
                { id: позиция.id, priceId: запись.id, reason: причина.trim() },
                {
                  onSuccess: () => {
                    setПричина('');
                    setУбираем(false);
                  },
                },
              );
            }}
          >
            {убрать.isPending ? 'Убираем…' : 'Убрать строку'}
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => {
              setУбираем(false);
              setПричина('');
              setЗапрет(null);
            }}
          >
            Отмена
          </Button.Root>
        </div>
      )}

      {запрет !== null && <Hint.Root hasError>{запрет}</Hint.Root>}
      {убрать.error !== null && <ErrorAlert error={убрать.error} />}
    </li>
  );
}
